/* eslint-disable no-plusplus */
import { Value } from 'react-calendar/dist/cjs/shared/types';

export function isValidDateString(dateStr: string) {
    // Проверяем формат "DD.MM.YYYY" (но не валидирует саму дату, например, 99.99.9999 пройдет)
    const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;
    return datePattern.test(dateStr);
}

export function getDateWithDashes(dateStr: string) {
    if (!dateStr) {
        return '';
    }

    const [day, month, year] = dateStr.split('.');

    return `${year}-${month}-${day}`;
}

export function isEndDateWithin30Years(startDate: Date, endDate: Date): boolean {
    const maxEndDate = new Date(startDate);
    maxEndDate.setFullYear(maxEndDate.getFullYear() + 30);
    return endDate <= maxEndDate;
}

export function isEndDateWithin1Year(startDate: Date, endDate: Date): boolean {
    const maxEndDate = new Date(startDate);
    maxEndDate.setFullYear(maxEndDate.getFullYear() + 1);
    return endDate >= maxEndDate;
}

export function isCreditDateAtLeastOneDayBefore(creditDate: Date, policyStartDate: Date) {
    // Создаем копии дат, чтобы не изменять оригинальные значения
    const creditDateCopy = new Date(creditDate);
    const policyStartDateCopy = new Date(policyStartDate);

    // Устанавливаем время на 00:00:00 для обеих дат, чтобы сравнивать только даты
    creditDateCopy.setHours(0, 0, 0, 0);
    policyStartDateCopy.setHours(0, 0, 0, 0);

    // Добавляем один день к creditDate, чтобы проверить "как минимум на 1 день"
    creditDateCopy.setDate(creditDateCopy.getDate() + 1);

    // Проверяем, что creditDate + 1 день <= policyStartDate
    return creditDateCopy <= policyStartDateCopy;
}

export function getCorrectFormatDate(date: string): Date | '' {
    if (!date || typeof date !== 'string') {
        return ''; // или можно вернуть дефолтное значение, например, new Date().getDate()
    }

    // Разбиваем строку по точке
    const parts = date.split('.');

    // Если строка не в формате "DD.MM.YYYY", возвращаем null или обрабатываем иначе
    if (parts.length !== 3) {
        return ''; // или можно попытаться парсить как "DD", но это зависит от логики
    }

    const [day, month, year] = parts.map((part) => parseInt(part, 10));

    // Проверяем, что все части даты валидны
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(day) || isNaN(month) || isNaN(year)) {
        return '';
    }

    // Создаем Date (месяцы в JS 0-11, поэтому month - 1)
    const dateObj = new Date(year, month - 1, day);

    // Проверяем, что дата корректна (например, 31.02.2020 вернет 2 марта)
    if (dateObj.getFullYear() !== year || dateObj.getMonth() + 1 !== month || dateObj.getDate() !== day) {
        return ''; // невалидная дата
    }

    return dateObj;
}

export const getDateFormat = (date: Value | undefined) => {
    return date?.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(' г.', '').toString();
    // return date?.toString();
};

export const isValidDate = (year: number, month: number, day: number): boolean => {
    const date = new Date(year, month, day);
    return date.getFullYear() === year && date.getMonth() === month && date.getDate() === day;
};

export const isValidDateModal = (dateString: string) => {
    // Проверяем формат "DD.MM.YYYY" (но не валидирует саму дату, например, 99.99.9999 пройдет)
    const datePattern = /^\d{2}\.\d{2}\.\d{4}$/;

    return datePattern.test(dateString);
};

// Функция для вычисления возраста
export function calculateAgeCustom(birthDate: Date, endDate?: Date): number {
    const referenceDate = endDate || new Date(); // Если дата окончания не указана, используем текущую дату
    const birthYear = birthDate.getFullYear();
    const referenceYear = referenceDate.getFullYear();

    // Проверяем, был ли уже день рождения в этом году
    const birthMonth = birthDate.getMonth();
    const referenceMonth = referenceDate.getMonth();
    const birthDay = birthDate.getDate();
    const referenceDay = referenceDate.getDate();

    let age = referenceYear - birthYear;

    if (referenceMonth < birthMonth || (referenceMonth === birthMonth && referenceDay < birthDay)) {
        age--;
    }

    return age;
}

// Функция для вычисления возраста
export const calculateAge = (birthDate: Date, onDate: Date): number => {
    if (birthDate) {
        let age = onDate.getFullYear() - birthDate.getFullYear();
        const monthDifference = onDate.getMonth() - birthDate.getMonth();
        const dayDifference = onDate.getDate() - birthDate.getDate();

        // Если текущий месяц меньше месяца рождения,
        // или текущий месяц равен месяцу рождения, но текущий день меньше дня рождения,
        // уменьшаем возраст на 1
        if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
            age -= 1;
        }

        return age;
    }
    return NaN;
};

export const isoDate = (dateStr: string | Date) => {
    const preparedDate = dateStr instanceof Date ? dateStr : getCorrectFormatDate(dateStr);

    if (!preparedDate) {
        return '';
    }

    const date = new Date(preparedDate);

    // Форматируем дату и время в нужный формат
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы в JavaScript от 0 до 11
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const milliseconds = String(date.getMilliseconds()).padStart(3, '0');

    // Собираем строку в нужном формате
    const localDateString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    return localDateString;
};

export const getValidateDateFormat = (dateString: string) => {
    // Разделение строки на составляющие
    const [day, month, year] = dateString.split('.').map(Number);

    // Создание объекта Date (месяцы в JavaScript начинаются с 0)
    const date = new Date(year, month - 1, day);

    // Преобразование даты в нужный формат
    return date;
};

export const formatDateToDDMMYYYY = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
};

export const localDate = (date: string | null | undefined) => (date ? new Date(date).toLocaleDateString('ru-RU') : null);

export const getTimeFormat = (totalSeconds: number): string => {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export function DifferinceYears(startDate: any, endDate: any) {
    // Преобразуем строки в объекты Date
    if (startDate instanceof Date && endDate instanceof Date) {
        startDate = new Date(startDate);
        endDate = new Date(endDate);

        // Вычисляем разницу в годах
        let yearsDifference = endDate.getFullYear() - startDate.getFullYear();

        // Проверяем, прошёл startDate в конечном году
        const isBeforeStartDay =
            endDate.getMonth() < startDate.getMonth() ||
            (endDate.getMonth() === startDate.getMonth() && endDate.getDate() < startDate.getDate());

        // Если startDate еще не прошел в этом году, уменьшаем разницу на 1
        if (isBeforeStartDay) {
            // eslint-disable-next-line no-plusplus
            yearsDifference--;
        }

        // Проверяем, если разница лет больше 14
        return yearsDifference;
    }
}

export function replaceTimeWithMidnight(currentDate: Date) {
    const dateString = currentDate.toString().replace(/\d{2}:\d{2}:\d{2}/, '00:00:00');
    const date = new Date(dateString);
    return date;
}

export function isAtLeastTomorrow(date: Date | string) {
    if (!(date instanceof Date)) {
        return false;
    }
    // Получаем текущую дату без времени
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Завтрашняя дата
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    // Сравниваем
    return date >= tomorrow;
}

export function isTodayOrEarlier(date: Date | string) {
    if (!(date instanceof Date)) {
        return false;
    }

    const today = new Date();

    return date < today;
}
function formatDate(date: Date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${month}-${day}`;
}
export const prepareDate = (dateStr: string) => {
    const [day, month, year] = dateStr.split('.');

    return `${year}-${month}-${day}`;
};
function formatFullDate(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}
export function getWeekendsDates() {
    const year = new Date().getFullYear();
    const weekends = [];
    const fixedHolidays = [
        `01-01`,
        `01-02`,
        `01-03`,
        `01-04`,
        `01-05`,
        `01-06`,
        `01-07`,
        `01-08`,
        `02-23`,
        `03-08`,
        `05-01`,
        `05-09`,
        `06-12`,
        `11-04`,
    ];

    const floatingHolidays = [''];

    for (let month = 0; month < 12; month++) {
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dayOfWeek = date.getDay();
            const dateStr = formatDate(date); // Форматируем дату в "MM-DD"

            // Проверяем, является ли день выходным (суббота или воскресенье)
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

            // Проверяем, является ли день праздником (фиксированным или плавающим)
            const isFixedHoliday = fixedHolidays.includes(dateStr);
            const isFloatingHoliday = floatingHolidays.includes(formatFullDate(date));

            if (isWeekend || isFixedHoliday || isFloatingHoliday) {
                weekends.push(new Date(date));
            }
        }
    }

    return weekends;
}
export function isToday(dateStr: string): boolean {
    if (!dateStr) {
        return false;
    }

    const [day, month, year] = dateStr.split('.');
    const inputDate = new Date(`${year}-${month}-${day}`);

    const today = new Date();

    return (
        inputDate.getDate() === today.getDate() &&
        inputDate.getMonth() === today.getMonth() &&
        inputDate.getFullYear() === today.getFullYear()
    );
}
export function isTodayAndAfter4pm() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();

    return hours > 16 || (hours === 16 && minutes > 0) || (hours === 16 && seconds > 0);
}
export function isWeekday(date: Date | string) {
    if (!(date instanceof Date) || !date) {
        return false;
    }
    const day = date.getDay(); // 0 — воскресенье, 6 — суббота

    return day >= 1 && day <= 5;
}
export function parseDirtyDateString(dirtyDateString: string): Date | string {
    if (!dirtyDateString) {
        return dirtyDateString;
    }

    const numbers = dirtyDateString.match(/\d+/g);

    if (!numbers || numbers.length < 3) {
        return dirtyDateString;
    }

    const [day, month, year] = numbers.map(Number);
    const date = new Date(year, month - 1, day);

    const isDateValid = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;

    if (!isDateValid) {
        return dirtyDateString;
    }

    return date;
}
